import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const translations = {
  EN: {
    // Navigation
    nav: {
      home: 'Home',
      services: 'Services',
      about: 'About',
      approach: 'Approach',
      contact: 'Contact',
    },
    // Hero
    hero: {
      location: 'Calvià · Mallorca',
      headline: 'Mallorca Property Management — Private & Structured',
      subheadline: 'Local presence, transparent reporting. Long-Term Property Protection.',
      ctaPrimary: 'Request a Free Consultation',
      ctaSecondary: 'See Our Services',
    },
    // Services Overview
    servicesOverview: {
      label: 'What We Do',
      title: 'Structured Oversight for Your Property',
      description: 'We provide comprehensive property management and coordination services for international homeowners in Mallorca. Preventive oversight, reliable execution, and transparent reporting.',
      viewAll: 'View All Services',
    },
    // Service Titles
    servicesTitles: {
      propertyManagement: 'Private Property Management',
      gardenPool: 'Garden & Pool',
      renovations: 'Renovations & Technical Works',
      propertySupervision: 'Property Oversight',
      maintenanceCoordination: 'Technical Maintenance Coordination',
      concierge: 'Concierge Services',
      vehicle: 'Vehicle Management',
      yacht: 'Yacht Maintenance Coordination',
    },
    // Why Us
    whyUs: {
      label: 'Why Balearic Home Solutions',
      title: 'Private Management. Structured Oversight. Peace of Mind.',
      description: 'Managing a property from abroad requires reliable local oversight. We provide structured supervision, coordinated maintenance, and transparent communication—ensuring your Mallorca property is protected year-round.',
      points: [
        { title: 'Local Presence', desc: 'Based in Calvià with direct oversight across Southwest Mallorca' },
        { title: 'Preventive Focus', desc: 'Proactive monitoring to prevent issues before they escalate' },
        { title: 'Transparent Reporting', desc: 'Regular updates and documentation of all activities' },
        { title: 'Trusted Network', desc: 'Established relationships with reliable local contractors' },
      ],
    },
    // Testimonials
    testimonials: {
      label: 'Client Testimonials',
      title: 'Trusted by Property Owners',
    },
    // CTA
    cta: {
      title: 'Ready to Discuss Your Property?',
      description: 'Contact us for a private consultation about your property management needs in Mallorca.',
      buttonPrimary: 'Private Consultation',
      buttonSecondary: 'Our Approach',
    },
    // Services Page
    servicesPage: {
      label: 'Our Services',
      title: 'Comprehensive Property Services',
      description: 'We provide structured property management and technical coordination for international homeowners in Mallorca. Preventive oversight, reliable execution, and transparent reporting — ensuring your property remains perfectly maintained year-round.',
      whatWeDo: 'What We Do',
      typicalRequests: 'Typical Requests',
      requestConsultation: 'Request a Consultation',
      ctaTitle: 'Need a Custom Solution?',
      ctaDesc: 'Every property has unique requirements. Contact us to discuss a tailored management plan for your Mallorca property.',
      getInTouch: 'Get in Touch',
    },
    // About Page
    aboutPage: {
      label: 'About Us',
      title: 'Local Expertise in Mallorca',
      description: 'Private property management for international homeowners. Structured oversight, preventive focus, long-term protection.',
      companyTitle: 'Balearic Home Solutions',
      companyText1: 'I founded Balearic Home Solutions to provide the kind of property management I would want for my own home. Based in Calvià, I work directly with each client to ensure their Mallorca property receives consistent, hands-on oversight—not delegated tasks passed through layers of staff.',
      companyText2: 'Since 2016, I have been directly involved in residential property supervision across Southwest Mallorca. This practical experience has shaped my approach: preventive oversight to catch issues early, transparent cost control, and decisions made with long-term asset preservation in mind.',
      companyText3: 'I understand the challenges of managing a property from abroad—the uncertainty, the communication gaps, the worry about what you cannot see. My commitment is simple: regular presence at your property, honest reporting, and the peace of mind that comes from knowing someone reliable is looking after your investment.',
      tagline: '"Private Management. Structured Oversight. Peace of Mind."',
      serviceAreaTitle: 'Serving Southwest Mallorca',
      serviceAreaText1: 'Based in Calvià on the southwest coast, we provide property management services throughout the region. Our local presence means rapid response times, established contractor relationships, and deep knowledge of the area.',
      serviceAreaText2: 'We serve property owners in Calvià, Santa Ponsa, Port d\'Andratx, Camp de Mar, Costa de la Calma, Sol de Mallorca, Portals Nous, Bendinat, Son Vida, and surrounding areas.',
      areasWeServe: 'Areas We Serve',
      principlesTitle: 'Our Principles',
      principlesDesc: 'The operational standards that guide how we manage your property.',
      ctaTitle: 'Let\'s Discuss Your Property',
      ctaDesc: 'Contact us for a private consultation about your property management requirements in Mallorca.',
    },
    // Approach Page
    approachPage: {
      label: 'Our Approach',
      title: 'Structured Property Oversight',
      description: 'A structured operational framework designed to ensure reliable supervision and long-term protection of your Mallorca property.',
      steps: [
        { title: 'Assessment', description: 'We begin with a thorough evaluation of your property and your specific requirements. This includes an on-site inspection, documentation of current condition, identification of immediate needs, and understanding your priorities and concerns.' },
        { title: 'Planning', description: 'Based on the assessment, we develop a structured plan tailored to your property. This covers regular oversight schedules, maintenance priorities, contractor recommendations, and communication protocols.' },
        { title: 'Execution', description: 'We coordinate and oversee all agreed activities. This includes scheduling contractors, managing access, supervising work quality, and handling day-to-day requirements as they arise.' },
        { title: 'Supervision', description: 'Regular property visits ensure ongoing oversight. We check property condition, verify that scheduled work has been completed correctly, and identify any emerging issues requiring attention.' },
        { title: 'Reporting', description: 'Transparent communication through regular reports. You receive updates on property status, completed work, upcoming requirements, and any recommendations—keeping you fully informed.' },
      ],
      keyActivities: 'Key Activities',
      benefitsTitle: 'Why This Approach Works',
      benefitsDesc: 'A structured framework ensures nothing is overlooked and provides clear accountability at every stage.',
      ctaTitle: 'Ready to Get Started?',
      ctaDesc: 'Contact us to discuss how we can apply this approach to your Mallorca property.',
      viewServices: 'View Our Services',
    },
    // Contact Page
    contactPage: {
      label: 'Contact Us',
      title: 'Get in Touch',
      description: 'Ready to discuss your property management needs in Mallorca? Contact us for a private consultation.',
      contactInfo: 'Contact Information',
      location: 'Location',
      email: 'Email',
      phone: 'Phone',
      responseTime: 'Response Time',
      responseTimeText: 'We typically respond to enquiries within 24 hours during business days. For urgent matters, please call directly.',
      form: {
        fullName: 'Full Name',
        emailLabel: 'Email',
        phoneLabel: 'Phone',
        propertyLocation: 'Property Location (Area in Mallorca)',
        propertyType: 'Property Type',
        serviceRequired: 'Service Required',
        message: 'Message',
        messagePlaceholder: 'Tell us about your property and requirements...',
        submit: 'Send Message',
        sending: 'Sending...',
        required: '*',
        selectArea: 'Select area',
        selectPropertyType: 'Select property type',
        selectService: 'Select service required',
        privacyNote: 'By submitting this form, you agree to be contacted regarding your enquiry. We respect your privacy and will not share your information with third parties.',
      },
      successTitle: 'Thank You',
      successMessage: 'Thank you. Your message has been received and we will contact you shortly.',
      sendAnother: 'Send Another Message',
      mapTitle: 'Based in Calvià, Mallorca',
      mapDesc: 'Serving property owners across Southwest Mallorca, from Calvià and Santa Ponsa to Port d\'Andratx, Son Vida, and beyond.',
    },
    // Footer
    footer: {
      description: 'Private property management in Mallorca. Structured local oversight for international homeowners seeking reliable, professional supervision of their residential properties.',
      trustedBy: 'Trusted by property owners and professionals across Southwest Mallorca.',
      services: 'Services',
      company: 'Company',
      areas: 'Areas',
      aboutUs: 'About Us',
      ourApproach: 'Our Approach',
      allServices: 'All Services',
      copyright: '© {year} Balearic Home Solutions. All rights reserved.',
      tagline: 'Private Property Management · Calvià · Southwest Mallorca',
    },
    // Property Management Mallorca Page
    propertyManagementMallorca: {
      hero: {
        label: 'Property Management Mallorca',
        title: 'Mallorca Property Management',
        subtitle: 'Private, structured property management for international homeowners across Mallorca.',
        cta: 'Get in Touch',
      },
      introduction: {
        title: 'Why Professional Property Management in Mallorca?',
        introLine: 'Owning a property in Mallorca from abroad comes with real challenges — maintenance, contractors, seasonal preparation and unexpected issues.',
        text1: 'Balearic Home Solutions provides structured Mallorca property management for international homeowners, acting as your single trusted point of contact on the island.',
        text2: 'Based in Calvià, in Southwest Mallorca, we conduct regular inspections, coordinate works and keep you informed with transparent reporting.',
        locationLabel: 'Southwest Mallorca',
      },
      services: {
        label: 'Our Services',
        title: 'Our Mallorca Property Management Services',
        description: 'We provide end-to-end property management services tailored to the needs of international homeowners in Mallorca.',
        items: [
          { title: 'Property Oversight', desc: 'Regular inspections, condition monitoring, storm checks, and detailed photo reports to keep you informed about your property at all times.' },
          { title: 'Maintenance Coordination', desc: 'We manage contractors, compare quotes, and ensure work is done properly — without you having to chase anyone.' },
          { title: 'Concierge & Preparation', desc: 'Pre-arrival preparation, provisioning, key holding, and coordination of services for your stays in Mallorca.' },
        ],
        viewAll: 'View All Services',
      },
      managing: {
        label: 'On the Ground',
        title: 'Managing a Property in Mallorca',
        intro1: 'Looking after a home in Mallorca involves more than occasional attention. Properties here face intense summer sun, humidity, sea air and seasonal storms — and small issues left unattended can quickly become expensive ones.',
        intro2: 'These challenges affect international second-home owners and permanent residents alike. Whether you are away for months at a time or simply prefer professional oversight of your property, the practical work remains the same:',
        items: [
          { title: 'Regular Inspections', desc: 'Scheduled visits during periods of absence to check the condition of the house, its installations and exterior areas.' },
          { title: 'Preventive Maintenance', desc: 'Addressing wear before it becomes damage — from roof and drainage checks to shutters, joinery and installations.' },
          { title: 'Contractor Coordination', desc: 'Selecting, scheduling and supervising local trades, and verifying that work is completed to standard.' },
          { title: 'Garden & Pool Oversight', desc: 'Ensuring outdoor areas and pool systems are properly maintained by the right specialists throughout the year.' },
          { title: 'Seasonal Preparation', desc: 'Preparing the property for seasonal changes, periods of occupancy and extended absences.' },
          { title: 'Technical Issues', desc: 'Responding to breakdowns, leaks and unexpected problems with a clear process and trusted technicians.' },
          { title: 'Pre-Arrival Preparation', desc: 'Having the house cleaned, aired, stocked and fully ready before you or your guests arrive.' },
          { title: 'A Local Point of Contact', desc: 'One reliable person on the island who knows your property and acts on your behalf.' },
        ],
      },
      process: {
        label: 'How It Works',
        title: 'How Our Property Management Works',
        description: 'A clear, transparent process from first contact to ongoing oversight.',
        steps: [
          { title: 'Initial Consultation', desc: 'We discuss your property, how you use it and what level of oversight you are looking for.' },
          { title: 'Property Assessment', desc: 'An on-site visit to document the current condition of the property, its installations and any immediate needs.' },
          { title: 'Tailored Management Plan', desc: 'A management structure adapted to your property and requirements.' },
          { title: 'Ongoing Oversight & Coordination', desc: 'Regular property inspections, maintenance coordination and supervision of any works, handled locally.' },
          { title: 'Reporting & Communication', desc: 'Regular photo reports and clear updates, keeping you informed about your property wherever you are.' },
        ],
      },
      whoWeWork: {
        label: 'Who We Work With',
        title: 'Owners We Support',
        description: 'Our property management is tailored to each property and owner\'s requirements. We work with:',
        types: [
          'Second-home owners',
          'International homeowners',
          'Permanent residents who want professional oversight of their property',
          'Owners who spend extended periods away from Mallorca',
          'Owners of villas and high-value private residences requiring structured local management',
        ],
      },
      faq: {
        label: 'FAQ',
        title: 'Frequently Asked Questions',
        items: [
          { q: 'How much does property management in Mallorca cost?', a: 'Costs depend on the size and location of your property and the frequency of visits you require. After an initial consultation, we prepare a tailored proposal with transparent pricing and no hidden fees.' },
          { q: 'Which areas of Mallorca do you cover?', a: 'We are based in Calvià and focus on Southwest Mallorca — including Bendinat, Sol de Mallorca, Portals Nous, Santa Ponsa, Port d\'Andratx, Son Vida and Palma. Other areas can be covered on request.' },
          { q: 'What is included in your property management service?', a: 'Regular property inspections with photo reports, maintenance and contractor coordination, key holding, storm checks and pre-arrival preparation. The exact scope is adapted to your property and your needs.' },
          { q: 'Do I need property management if I only visit Mallorca in summer?', a: 'Yes — the months a property stands empty are when issues develop unnoticed: leaks, humidity, storm damage or garden decline. Regular local oversight detects problems early and protects the long-term value of your investment.' },
          { q: 'How often do you visit the property?', a: 'Visit frequency is agreed individually and depends on the property, how it is used and your preferences. Some owners want frequent inspections, others a lighter schedule with additional checks after storms or before arrivals. The structure is adapted to each property.' },
          { q: 'What happens in case of an emergency?', a: 'If an issue arises, we assess the situation on site, inform you promptly and coordinate the appropriate specialists. You are kept informed throughout, and significant interventions are agreed with you before work is carried out — unless immediate action is needed to prevent further damage.' },
          { q: 'Do you work with a fixed management fee or per service?', a: 'Both structures are possible. Some owners prefer ongoing management, while others engage us for specific services or projects. After an initial consultation, we propose a structure adapted to your property and requirements.' },
        ],
      },
      closing: {
        title: 'Ready to Discuss Your Property?',
        description: 'We offer a direct and professional approach tailored to your specific needs. Get in touch to discuss your property and explore how we can support you locally.',
        ctaPrimary: 'Request a Private Consultation',
      },
    },
    // Bendinat Page
    bendinatPage: {
      hero: {
        location: 'Bendinat · Mallorca',
        title: 'Property Management in Bendinat',
        subtitle: 'Private, structured oversight for international homeowners in one of Southwest Mallorca\'s most exclusive residential areas.',
        cta: 'Request a Private Consultation',
      },
      whyBendinat: {
        label: 'The Area',
        title: 'Why Bendinat?',
        text1: 'Bendinat is one of the most prestigious residential areas in Southwest Mallorca, located just west of Palma. Known for its luxury villas, mature gardens, and exceptional privacy, it attracts discerning international homeowners seeking a refined Mediterranean lifestyle.',
        text2: 'The area offers proximity to Port Adriano, one of the Mediterranean\'s most exclusive marinas, as well as Portals Nous and the renowned golf courses of the region. Properties here represent significant investments that require attentive, professional oversight.',
        text3: 'For owners who are often abroad, having a trusted local presence to manage, maintain, and protect their Bendinat property is essential.',
      },
      services: {
        label: 'What We Offer',
        title: 'Our Services in Bendinat',
        description: 'We provide comprehensive property management tailored to the high standards expected in Bendinat.',
        viewAll: 'View All Services',
      },
      faq: {
        label: 'FAQ',
        title: 'Frequently Asked Questions',
        items: [
          { q: 'Do you manage properties in Bendinat while owners are abroad?', a: 'Yes. We act as your trusted local presence in Bendinat — conducting regular inspections, coordinating maintenance and sending detailed photo reports so you always know the real condition of your home. We are based in Calvià, just minutes away.' },
          { q: 'How often will you visit my Bendinat property?', a: 'Visit frequency is tailored to your property and preferences — weekly or bi-weekly schedules are most common. We also carry out additional checks after storms or heavy rain.' },
          { q: 'Can you supervise renovations in Bendinat while I am away?', a: 'Yes. We compare quotes from trusted local contractors, supervise the work on site and document progress with photos, so your project advances without you needing to be on the island.' },
          { q: 'Do you prepare the property before our arrival?', a: 'Yes. We coordinate cleaning, ventilation, provisioning, and pool and garden preparation so your Bendinat home is ready the moment you arrive.' },
        ],
      },
      cta: {
        title: 'Own a Property in Bendinat?',
        description: 'Contact us for a private consultation about your property management requirements. We\'re based locally in Calvià and provide hands-on oversight for Bendinat homeowners.',
        button: 'Request a Private Consultation',
      },
    },
    // Sol de Mallorca Page
    solDeMallorcaPage: {
      hero: {
        location: 'Sol de Mallorca',
        title: 'Property Management in Sol de Mallorca',
        subtitle: 'Dedicated local oversight for international homeowners in this quiet, exclusive residential area of Southwest Mallorca.',
        cta: 'Request a Private Consultation',
      },
      whySolDeMallorca: {
        label: 'The Area',
        title: 'Why Sol de Mallorca?',
        text1: 'Sol de Mallorca is a small, peaceful residential enclave nestled between Bendinat and Portals Nous on Mallorca\'s southwest coast. Known for its tranquility and privacy, it has become increasingly popular with international buyers seeking a refined retreat away from the busier tourist areas.',
        text2: 'The area offers stunning sea views, easy access to Palma (just 15 minutes), and proximity to some of the island\'s best beaches, golf courses, and marinas. Properties here tend to be high-quality villas that represent significant investments requiring attentive year-round management.',
        text3: 'For owners who spend much of the year abroad, having a trusted local presence to oversee, maintain, and protect their Sol de Mallorca property is essential.',
      },
      services: {
        label: 'What We Offer',
        title: 'Our Services in Sol de Mallorca',
        description: 'We provide comprehensive property management tailored to the needs of Sol de Mallorca homeowners.',
        viewAll: 'View All Services',
      },
      faq: {
        label: 'FAQ',
        title: 'Frequently Asked Questions',
        items: [
          { q: 'Do you offer property management in Sol de Mallorca?', a: 'Yes. Our base in Calvià is just minutes from Sol de Mallorca, allowing us to provide regular inspections, maintenance coordination and transparent reporting for homeowners in the area.' },
          { q: 'How do you protect a coastal property that stands empty for months?', a: 'Sea air and humidity are demanding on coastal villas. We ventilate the property regularly, monitor for damp and corrosion, check after storms and resolve small issues before they escalate into costly damage.' },
          { q: 'Can you manage our pool and garden year-round?', a: 'Yes. We coordinate trusted local pool and garden specialists and supervise their work, ensuring your outdoor areas remain in perfect condition throughout the year.' },
          { q: 'How do we stay informed while we are abroad?', a: 'You have a single point of contact and receive detailed photo reports after every visit. We are always reachable by email, phone or WhatsApp.' },
        ],
      },
      cta: {
        title: 'Own a Property in Sol de Mallorca?',
        description: 'Contact us for a private consultation about your property management requirements. We\'re based locally in Calvià and provide hands-on oversight for Sol de Mallorca homeowners.',
        button: 'Request a Private Consultation',
      },
    },
    // Areas We Serve
    areasWeServe: {
      label: 'Where We Operate',
    },
    // Property Types
    propertyTypes: ['Villa', 'Apartment', 'Townhouse', 'Finca / Country Estate', 'Community Property', 'Yacht', 'Other'],
    // Service Options
    serviceOptions: ['Property Management', 'Garden & Pool', 'Renovations & Technical Works', 'Property Supervision', 'Maintenance Coordination', 'Concierge Services', 'Vehicle Management', 'Yacht Maintenance Coordination', 'Multiple Services', 'General Enquiry'],
    // Mallorca Areas
    mallorcaAreas: ['Calvià', 'Santa Ponsa', 'Port d\'Andratx', 'Camp de Mar', 'Costa de la Calma', 'Sol de Mallorca', 'Portals Nous', 'Bendinat', 'Son Vida', 'Palmanova', 'Illetes', 'Cas Català', 'Palma', 'Other Southwest Mallorca', 'Other Area'],
  },
  ES: {
    // Navigation
    nav: {
      home: 'Inicio',
      services: 'Servicios',
      about: 'Nosotros',
      approach: 'Metodología',
      contact: 'Contacto',
    },
    // Hero
    hero: {
      location: 'Calvià · Mallorca',
      headline: 'Gestión de Propiedades en Mallorca — Privada y Estructurada',
      subheadline: 'Presencia local, informes transparentes. Protección a Largo Plazo.',
      ctaPrimary: 'Solicitar Consulta Gratuita',
      ctaSecondary: 'Ver Nuestros Servicios',
    },
    // Services Overview
    servicesOverview: {
      label: 'Lo Que Hacemos',
      title: 'Supervisión Estructurada para Su Propiedad',
      description: 'Proporcionamos servicios integrales de gestión y coordinación de propiedades para propietarios internacionales en Mallorca. Supervisión preventiva, ejecución fiable e informes transparentes.',
      viewAll: 'Ver Todos los Servicios',
    },
    // Service Titles
    servicesTitles: {
      propertyManagement: 'Gestión Privada de Propiedades',
      gardenPool: 'Jardín y Piscina',
      renovations: 'Reformas y Trabajos Técnicos',
      propertySupervision: 'Supervisión de Propiedades',
      maintenanceCoordination: 'Coordinación de Mantenimiento Técnico',
      concierge: 'Servicios de Conserjería',
      vehicle: 'Gestión de Vehículos',
      yacht: 'Coordinación de Mantenimiento de Yates',
    },
    // Why Us
    whyUs: {
      label: 'Por Qué Balearic Home Solutions',
      title: 'Gestión Privada. Supervisión Estructurada. Tranquilidad.',
      description: 'Gestionar una propiedad desde el extranjero requiere una supervisión local fiable. Proporcionamos supervisión estructurada, mantenimiento coordinado y comunicación transparente—asegurando que su propiedad en Mallorca esté protegida todo el año.',
      points: [
        { title: 'Presencia Local', desc: 'Ubicados en Calvià con supervisión directa en todo el suroeste de Mallorca' },
        { title: 'Enfoque Preventivo', desc: 'Monitorización proactiva para prevenir problemas antes de que escalen' },
        { title: 'Informes Transparentes', desc: 'Actualizaciones regulares y documentación de todas las actividades' },
        { title: 'Red de Confianza', desc: 'Relaciones establecidas con contratistas locales de confianza' },
      ],
    },
    // Testimonials
    testimonials: {
      label: 'Testimonios de Clientes',
      title: 'Confianza de los Propietarios',
    },
    // CTA
    cta: {
      title: '¿Listo para Hablar de Su Propiedad?',
      description: 'Contáctenos para una consulta privada sobre sus necesidades de gestión de propiedades en Mallorca.',
      buttonPrimary: 'Consulta Privada',
      buttonSecondary: 'Nuestra Metodología',
    },
    // Services Page
    servicesPage: {
      label: 'Nuestros Servicios',
      title: 'Servicios Integrales de Propiedad',
      description: 'Ofrecemos gestión estructurada de propiedades y coordinación técnica para propietarios internacionales en Mallorca. Supervisión preventiva, ejecución fiable e informes transparentes para garantizar que su propiedad se mantenga en perfecto estado durante todo el año.',
      whatWeDo: 'Lo Que Hacemos',
      typicalRequests: 'Solicitudes Típicas',
      requestConsultation: 'Solicitar Consulta',
      ctaTitle: '¿Necesita una Solución Personalizada?',
      ctaDesc: 'Cada propiedad tiene requisitos únicos. Contáctenos para discutir un plan de gestión adaptado a su propiedad en Mallorca.',
      getInTouch: 'Contactar',
    },
    // About Page
    aboutPage: {
      label: 'Nosotros',
      title: 'Experiencia Local en Mallorca',
      description: 'Gestión privada de propiedades para propietarios internacionales. Supervisión estructurada, enfoque preventivo, protección a largo plazo.',
      companyTitle: 'Balearic Home Solutions',
      companyText1: 'Fundé Balearic Home Solutions para ofrecer el tipo de gestión de propiedades que yo mismo querría para mi hogar. Con sede en Calvià, trabajo directamente con cada cliente para asegurar que su propiedad en Mallorca reciba una supervisión constante y práctica—sin tareas delegadas a través de capas de personal.',
      companyText2: 'Desde 2016, he estado directamente involucrado en la supervisión de propiedades residenciales en el suroeste de Mallorca. Esta experiencia práctica ha moldeado mi enfoque: supervisión preventiva para detectar problemas a tiempo, control de costes transparente, y decisiones tomadas con la preservación del activo a largo plazo en mente.',
      companyText3: 'Entiendo los desafíos de gestionar una propiedad desde el extranjero—la incertidumbre, las brechas en la comunicación, la preocupación por lo que no puede ver. Mi compromiso es simple: presencia regular en su propiedad, informes honestos, y la tranquilidad que viene de saber que alguien de confianza está cuidando su inversión.',
      tagline: '"Gestión Privada. Supervisión Estructurada. Tranquilidad."',
      serviceAreaTitle: 'Sirviendo el Suroeste de Mallorca',
      serviceAreaText1: 'Con sede en Calvià en la costa suroeste, proporcionamos servicios de gestión de propiedades en toda la región. Nuestra presencia local significa tiempos de respuesta rápidos, relaciones establecidas con contratistas y profundo conocimiento de la zona.',
      serviceAreaText2: 'Servimos a propietarios en Calvià, Santa Ponsa, Port d\'Andratx, Camp de Mar, Costa de la Calma, Sol de Mallorca, Portals Nous, Bendinat, Son Vida, y alrededores.',
      areasWeServe: 'Áreas que Servimos',
      principlesTitle: 'Nuestros Principios',
      principlesDesc: 'Los estándares operativos que guían cómo gestionamos su propiedad.',
      ctaTitle: 'Hablemos de Su Propiedad',
      ctaDesc: 'Contáctenos para una consulta privada sobre sus requisitos de gestión de propiedades en Mallorca.',
    },
    // Approach Page
    approachPage: {
      label: 'Nuestra Metodología',
      title: 'Supervisión Estructurada de Propiedades',
      description: 'Un marco operativo estructurado diseñado para garantizar una supervisión fiable y la protección a largo plazo de su propiedad en Mallorca.',
      steps: [
        { title: 'Evaluación', description: 'Comenzamos con una evaluación exhaustiva de su propiedad y sus requisitos específicos. Esto incluye una inspección in situ, documentación del estado actual, identificación de necesidades inmediatas y comprensión de sus prioridades y preocupaciones.' },
        { title: 'Planificación', description: 'Basándonos en la evaluación, desarrollamos un plan estructurado adaptado a su propiedad. Esto cubre horarios de supervisión regulares, prioridades de mantenimiento, recomendaciones de contratistas y protocolos de comunicación.' },
        { title: 'Ejecución', description: 'Coordinamos y supervisamos todas las actividades acordadas. Esto incluye programar contratistas, gestionar accesos, supervisar la calidad del trabajo y manejar los requisitos diarios según surjan.' },
        { title: 'Supervisión', description: 'Las visitas regulares a la propiedad aseguran una supervisión continua. Comprobamos el estado de la propiedad, verificamos que el trabajo programado se haya completado correctamente e identificamos cualquier problema emergente que requiera atención.' },
        { title: 'Informes', description: 'Comunicación transparente a través de informes regulares. Recibe actualizaciones sobre el estado de la propiedad, trabajos completados, requisitos próximos y cualquier recomendación—manteniéndole totalmente informado.' },
      ],
      keyActivities: 'Actividades Clave',
      benefitsTitle: 'Por Qué Funciona Este Enfoque',
      benefitsDesc: 'Un marco estructurado asegura que nada se pase por alto y proporciona una responsabilidad clara en cada etapa.',
      ctaTitle: '¿Listo para Empezar?',
      ctaDesc: 'Contáctenos para discutir cómo podemos aplicar este enfoque a su propiedad en Mallorca.',
      viewServices: 'Ver Nuestros Servicios',
    },
    // Contact Page
    contactPage: {
      label: 'Contacto',
      title: 'Póngase en Contacto',
      description: '¿Listo para discutir sus necesidades de gestión de propiedades en Mallorca? Contáctenos para una consulta privada.',
      contactInfo: 'Información de Contacto',
      location: 'Ubicación',
      email: 'Correo Electrónico',
      phone: 'Teléfono',
      responseTime: 'Tiempo de Respuesta',
      responseTimeText: 'Normalmente respondemos a las consultas en 24 horas durante días laborables. Para asuntos urgentes, por favor llame directamente.',
      form: {
        fullName: 'Nombre Completo',
        emailLabel: 'Correo Electrónico',
        phoneLabel: 'Teléfono',
        propertyLocation: 'Ubicación de la Propiedad (Zona en Mallorca)',
        propertyType: 'Tipo de Propiedad',
        serviceRequired: 'Servicio Requerido',
        message: 'Mensaje',
        messagePlaceholder: 'Cuéntenos sobre su propiedad y requisitos...',
        submit: 'Enviar Mensaje',
        sending: 'Enviando...',
        required: '*',
        selectArea: 'Seleccionar zona',
        selectPropertyType: 'Seleccionar tipo de propiedad',
        selectService: 'Seleccionar servicio requerido',
        privacyNote: 'Al enviar este formulario, acepta ser contactado respecto a su consulta. Respetamos su privacidad y no compartiremos su información con terceros.',
      },
      successTitle: 'Gracias',
      successMessage: 'Gracias. Su mensaje ha sido recibido y nos pondremos en contacto con usted en breve.',
      sendAnother: 'Enviar Otro Mensaje',
      mapTitle: 'Con Sede en Calvià, Mallorca',
      mapDesc: 'Sirviendo a propietarios en todo el suroeste de Mallorca, desde Calvià y Santa Ponsa hasta Port d\'Andratx, Son Vida y más allá.',
    },
    // Footer
    footer: {
      description: 'Gestión privada de propiedades en Mallorca. Supervisión local estructurada para propietarios internacionales que buscan supervisión fiable y profesional de sus propiedades residenciales.',
      trustedBy: 'De confianza para propietarios y profesionales en todo el suroeste de Mallorca.',
      services: 'Servicios',
      company: 'Empresa',
      areas: 'Zonas',
      aboutUs: 'Nosotros',
      ourApproach: 'Nuestra Metodología',
      allServices: 'Todos los Servicios',
      copyright: '© {year} Balearic Home Solutions. Todos los derechos reservados.',
      tagline: 'Gestión Privada de Propiedades · Calvià · Suroeste de Mallorca',
    },
    // Property Management Mallorca Page
    propertyManagementMallorca: {
      hero: {
        label: 'Gestión de Propiedades Mallorca',
        title: 'Gestión Privada de Propiedades en Mallorca',
        subtitle: 'Supervisión local discreta para propietarios internacionales.',
        cta: 'Contactar',
      },
      introduction: {
        title: '¿Por Qué una Gestión Profesional de Propiedades en Mallorca?',
        introLine: 'Poseer una propiedad en Mallorca desde el extranjero conlleva desafíos reales — mantenimiento, contratistas, preparación estacional e imprevistos.',
        text1: 'Balearic Home Solutions proporciona gestión estructurada de propiedades en Mallorca para propietarios internacionales, actuando como su único punto de contacto de confianza en la isla.',
        text2: 'Con sede en Calvià, en el suroeste de Mallorca, realizamos inspecciones regulares, coordinamos trabajos y le mantenemos informado con informes transparentes.',
        locationLabel: 'Suroeste de Mallorca',
      },
      services: {
        label: 'Nuestros Servicios',
        title: 'Nuestros Servicios de Gestión de Propiedades en Mallorca',
        description: 'Proporcionamos servicios completos de gestión de propiedades adaptados a las necesidades de propietarios internacionales en Mallorca.',
        items: [
          { title: 'Supervisión de Propiedades', desc: 'Inspecciones regulares, monitorización del estado, verificaciones tras tormentas e informes fotográficos detallados para mantenerle informado sobre su propiedad en todo momento.' },
          { title: 'Coordinación de Mantenimiento', desc: 'Gestionamos contratistas, comparamos presupuestos y nos aseguramos de que el trabajo se realice correctamente — sin que usted tenga que perseguir a nadie.' },
          { title: 'Conserjería y Preparación', desc: 'Preparación previa a la llegada, aprovisionamiento, custodia de llaves y coordinación de servicios para sus estancias en Mallorca.' },
        ],
        viewAll: 'Ver Todos los Servicios',
      },
      managing: {
        label: 'Sobre el Terreno',
        title: 'Gestionar una Propiedad en Mallorca',
        intro1: 'Cuidar una vivienda en Mallorca requiere más que atención ocasional. Las propiedades aquí se enfrentan al intenso sol del verano, la humedad, el aire marino y las tormentas estacionales — y los pequeños problemas desatendidos pueden convertirse rápidamente en costosos.',
        intro2: 'Estos desafíos afectan tanto a propietarios internacionales de segundas residencias como a residentes permanentes. Ya sea que pase meses fuera o simplemente prefiera una supervisión profesional de su propiedad, el trabajo práctico es el mismo:',
        items: [
          { title: 'Inspecciones Regulares', desc: 'Visitas programadas durante los períodos de ausencia para comprobar el estado de la casa, sus instalaciones y las zonas exteriores.' },
          { title: 'Mantenimiento Preventivo', desc: 'Atender el desgaste antes de que se convierta en daño — desde revisiones de tejado y drenajes hasta persianas, carpintería e instalaciones.' },
          { title: 'Coordinación de Contratistas', desc: 'Seleccionar, programar y supervisar a los profesionales locales, verificando que el trabajo se complete correctamente.' },
          { title: 'Supervisión de Jardín y Piscina', desc: 'Asegurar que las zonas exteriores y los sistemas de piscina se mantengan correctamente por los especialistas adecuados durante todo el año.' },
          { title: 'Preparación Estacional', desc: 'Preparar la propiedad para los cambios de estación, los períodos de ocupación y las ausencias prolongadas.' },
          { title: 'Incidencias Técnicas', desc: 'Responder a averías, fugas y problemas imprevistos con un proceso claro y técnicos de confianza.' },
          { title: 'Preparación Previa a la Llegada', desc: 'Tener la casa limpia, ventilada, abastecida y completamente lista antes de que llegue usted o sus invitados.' },
          { title: 'Un Punto de Contacto Local', desc: 'Una persona fiable en la isla que conoce su propiedad y actúa en su nombre.' },
        ],
      },
      process: {
        label: 'Cómo Funciona',
        title: 'Cómo Funciona Nuestra Gestión de Propiedades',
        description: 'Un proceso claro y transparente desde el primer contacto hasta la supervisión continua.',
        steps: [
          { title: 'Consulta Inicial', desc: 'Hablamos sobre su propiedad, cómo la utiliza y qué nivel de supervisión busca.' },
          { title: 'Evaluación de la Propiedad', desc: 'Una visita in situ para documentar el estado actual de la propiedad, sus instalaciones y cualquier necesidad inmediata.' },
          { title: 'Plan de Gestión Personalizado', desc: 'Una estructura de gestión adaptada a su propiedad y sus necesidades.' },
          { title: 'Supervisión y Coordinación Continuas', desc: 'Inspecciones regulares, coordinación del mantenimiento y supervisión de los trabajos, gestionado localmente.' },
          { title: 'Informes y Comunicación', desc: 'Informes fotográficos regulares y actualizaciones claras, manteniéndole informado sobre su propiedad esté donde esté.' },
        ],
      },
      whoWeWork: {
        label: 'Con Quién Trabajamos',
        title: 'Propietarios a los que Apoyamos',
        description: 'Nuestra gestión de propiedades se adapta a cada propiedad y a las necesidades de cada propietario. Trabajamos con:',
        types: [
          'Propietarios de segundas residencias',
          'Propietarios internacionales',
          'Residentes permanentes que desean una supervisión profesional de su propiedad',
          'Propietarios que pasan largos períodos fuera de Mallorca',
          'Propietarios de villas y residencias privadas de alto valor que requieren una gestión local estructurada',
        ],
      },
      faq: {
        label: 'Preguntas Frecuentes',
        title: 'Preguntas Frecuentes',
        items: [
          { q: '¿Cuánto cuesta la gestión de propiedades en Mallorca?', a: 'Los costes dependen del tamaño y la ubicación de su propiedad y de la frecuencia de visitas que necesite. Tras una consulta inicial, preparamos una propuesta personalizada con precios transparentes y sin costes ocultos.' },
          { q: '¿Qué zonas de Mallorca cubren?', a: 'Tenemos nuestra sede en Calvià y nos centramos en el suroeste de Mallorca — incluyendo Bendinat, Sol de Mallorca, Portals Nous, Santa Ponsa, Port d\'Andratx, Son Vida y Palma. Otras zonas bajo petición.' },
          { q: '¿Qué incluye su servicio de gestión de propiedades?', a: 'Inspecciones regulares con informes fotográficos, coordinación de mantenimiento y contratistas, custodia de llaves, verificaciones tras tormentas y preparación previa a la llegada. El alcance exacto se adapta a su propiedad y sus necesidades.' },
          { q: '¿Necesito gestión de propiedades si solo visito Mallorca en verano?', a: 'Sí — los meses en que una propiedad está vacía son cuando los problemas se desarrollan sin ser detectados: fugas, humedad, daños por tormentas o deterioro del jardín. La supervisión local regular detecta los problemas a tiempo y protege el valor de su inversión a largo plazo.' },
          { q: '¿Con qué frecuencia visitan la propiedad?', a: 'La frecuencia de las visitas se acuerda individualmente y depende de la propiedad, de cómo se utiliza y de sus preferencias. Algunos propietarios desean inspecciones frecuentes; otros, un calendario más ligero con verificaciones adicionales tras tormentas o antes de las llegadas. La estructura se adapta a cada propiedad.' },
          { q: '¿Qué ocurre en caso de emergencia?', a: 'Si surge un problema, evaluamos la situación in situ, le informamos con prontitud y coordinamos a los especialistas adecuados. Le mantenemos informado en todo momento, y las intervenciones significativas se acuerdan con usted antes de realizar el trabajo — salvo que sea necesaria una acción inmediata para evitar daños mayores.' },
          { q: '¿Trabajan con una tarifa de gestión fija o por servicio?', a: 'Ambas estructuras son posibles. Algunos propietarios prefieren una gestión continua, mientras que otros nos contratan para servicios o proyectos específicos. Tras una consulta inicial, proponemos una estructura adaptada a su propiedad y sus necesidades.' },
        ],
      },
      closing: {
        title: '¿Listo para Hablar de Su Propiedad?',
        description: 'Ofrecemos un enfoque directo y profesional adaptado a sus necesidades específicas. Póngase en contacto para hablar de su propiedad y explorar cómo podemos apoyarle localmente.',
        ctaPrimary: 'Solicitar Consulta Privada',
      },
    },
    // Bendinat Page
    bendinatPage: {
      hero: {
        location: 'Bendinat · Mallorca',
        title: 'Gestión de Propiedades en Bendinat',
        subtitle: 'Supervisión privada y estructurada para propietarios internacionales en una de las zonas residenciales más exclusivas del suroeste de Mallorca.',
        cta: 'Solicitar Consulta Privada',
      },
      whyBendinat: {
        label: 'La Zona',
        title: '¿Por Qué Bendinat?',
        text1: 'Bendinat es una de las zonas residenciales más prestigiosas del suroeste de Mallorca, situada al oeste de Palma. Conocida por sus villas de lujo, jardines maduros y excepcional privacidad, atrae a propietarios internacionales exigentes que buscan un estilo de vida mediterráneo refinado.',
        text2: 'La zona ofrece proximidad a Port Adriano, uno de los puertos deportivos más exclusivos del Mediterráneo, así como a Portals Nous y los renombrados campos de golf de la región. Las propiedades aquí representan inversiones significativas que requieren una supervisión atenta y profesional.',
        text3: 'Para los propietarios que a menudo están en el extranjero, tener una presencia local de confianza para gestionar, mantener y proteger su propiedad en Bendinat es esencial.',
      },
      services: {
        label: 'Lo Que Ofrecemos',
        title: 'Nuestros Servicios en Bendinat',
        description: 'Proporcionamos gestión integral de propiedades adaptada a los altos estándares esperados en Bendinat.',
        viewAll: 'Ver Todos los Servicios',
      },
      faq: {
        label: 'Preguntas Frecuentes',
        title: 'Preguntas Frecuentes',
        items: [
          { q: '¿Gestionan propiedades en Bendinat mientras los propietarios están en el extranjero?', a: 'Sí. Actuamos como su presencia local de confianza en Bendinat — realizando inspecciones regulares, coordinando el mantenimiento y enviando informes fotográficos detallados para que siempre conozca el estado real de su hogar. Estamos en Calvià, a pocos minutos.' },
          { q: '¿Con qué frecuencia visitarán mi propiedad en Bendinat?', a: 'La frecuencia de visitas se adapta a su propiedad y preferencias — los programas semanales o quincenales son los más comunes. También realizamos verificaciones adicionales tras tormentas o lluvias intensas.' },
          { q: '¿Pueden supervisar reformas en Bendinat mientras estoy fuera?', a: 'Sí. Comparamos presupuestos de contratistas locales de confianza, supervisamos el trabajo in situ y documentamos el progreso con fotografías, para que su proyecto avance sin necesidad de que esté en la isla.' },
          { q: '¿Preparan la propiedad antes de nuestra llegada?', a: 'Sí. Coordinamos la limpieza, ventilación, aprovisionamiento y preparación de piscina y jardín para que su hogar en Bendinat esté listo en el momento de su llegada.' },
        ],
      },
      cta: {
        title: '¿Tiene una Propiedad en Bendinat?',
        description: 'Contáctenos para una consulta privada sobre sus necesidades de gestión de propiedades. Estamos ubicados en Calvià y proporcionamos supervisión directa para propietarios de Bendinat.',
        button: 'Solicitar Consulta Privada',
      },
    },
    // Sol de Mallorca Page
    solDeMallorcaPage: {
      hero: {
        location: 'Sol de Mallorca',
        title: 'Gestión de Propiedades en Sol de Mallorca',
        subtitle: 'Supervisión local dedicada para propietarios internacionales en esta tranquila y exclusiva zona residencial del suroeste de Mallorca.',
        cta: 'Solicitar Consulta Privada',
      },
      whySolDeMallorca: {
        label: 'La Zona',
        title: '¿Por Qué Sol de Mallorca?',
        text1: 'Sol de Mallorca es un pequeño y tranquilo enclave residencial situado entre Bendinat y Portals Nous en la costa suroeste de Mallorca. Conocido por su tranquilidad y privacidad, se ha vuelto cada vez más popular entre compradores internacionales que buscan un refugio refinado alejado de las zonas turísticas más concurridas.',
        text2: 'La zona ofrece impresionantes vistas al mar, fácil acceso a Palma (solo 15 minutos), y proximidad a algunas de las mejores playas, campos de golf y puertos deportivos de la isla. Las propiedades aquí tienden a ser villas de alta calidad que representan inversiones significativas que requieren una gestión atenta durante todo el año.',
        text3: 'Para los propietarios que pasan gran parte del año en el extranjero, tener una presencia local de confianza para supervisar, mantener y proteger su propiedad en Sol de Mallorca es esencial.',
      },
      services: {
        label: 'Lo Que Ofrecemos',
        title: 'Nuestros Servicios en Sol de Mallorca',
        description: 'Proporcionamos gestión integral de propiedades adaptada a las necesidades de los propietarios de Sol de Mallorca.',
        viewAll: 'Ver Todos los Servicios',
      },
      faq: {
        label: 'Preguntas Frecuentes',
        title: 'Preguntas Frecuentes',
        items: [
          { q: '¿Ofrecen gestión de propiedades en Sol de Mallorca?', a: 'Sí. Nuestra sede en Calvià está a pocos minutos de Sol de Mallorca, lo que nos permite ofrecer inspecciones regulares, coordinación de mantenimiento e informes transparentes a los propietarios de la zona.' },
          { q: '¿Cómo protegen una propiedad costera que permanece vacía durante meses?', a: 'El aire marino y la humedad son exigentes con las villas costeras. Ventilamos la propiedad regularmente, controlamos la humedad y la corrosión, verificamos tras las tormentas y resolvemos pequeños problemas antes de que se conviertan en daños costosos.' },
          { q: '¿Pueden ocuparse de nuestra piscina y jardín todo el año?', a: 'Sí. Coordinamos especialistas locales de confianza en piscinas y jardines y supervisamos su trabajo, asegurando que sus zonas exteriores se mantengan en perfectas condiciones durante todo el año.' },
          { q: '¿Cómo nos mantenemos informados desde el extranjero?', a: 'Tiene un único punto de contacto y recibe informes fotográficos detallados después de cada visita. Siempre estamos disponibles por correo electrónico, teléfono o WhatsApp.' },
        ],
      },
      cta: {
        title: '¿Tiene una Propiedad en Sol de Mallorca?',
        description: 'Contáctenos para una consulta privada sobre sus necesidades de gestión de propiedades. Estamos ubicados en Calvià y proporcionamos supervisión directa para propietarios de Sol de Mallorca.',
        button: 'Solicitar Consulta Privada',
      },
    },
    // Areas We Serve
    areasWeServe: {
      label: 'Dónde Operamos',
    },
    // Property Types
    propertyTypes: ['Villa', 'Apartamento', 'Adosado', 'Finca / Casa de Campo', 'Propiedad Comunitaria', 'Yate', 'Otro'],
    // Service Options
    serviceOptions: ['Gestión de Propiedades', 'Jardín y Piscina', 'Reformas y Trabajos Técnicos', 'Supervisión de Propiedades', 'Coordinación de Mantenimiento', 'Servicios de Conserjería', 'Gestión de Vehículos', 'Coordinación de Mantenimiento de Yates', 'Múltiples Servicios', 'Consulta General'],
    // Mallorca Areas
    mallorcaAreas: ['Calvià', 'Santa Ponsa', 'Port d\'Andratx', 'Camp de Mar', 'Costa de la Calma', 'Sol de Mallorca', 'Portals Nous', 'Bendinat', 'Son Vida', 'Palmanova', 'Illetes', 'Cas Català', 'Palma', 'Otro Suroeste de Mallorca', 'Otra Zona'],
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'EN';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = translations[language];

  const toggleLanguage = (lang) => {
    setLanguage(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageProvider;
